/**
 * Admin integrations (ADR-0037; D305). The operator manages rotatable provider keys (email first) from
 * *Configurações › Integrações*: the key is **write-only** — the console shows only a masked hint + status,
 * never the key. This module is the framework-neutral, tested view-model: seed the editable form from the
 * server view, validate the from/reply-to addresses, and normalize into the wire `SetEmailIntegrationInput`
 * (a blank apiKey keeps the stored key). Thin seams wrap the real vello-api endpoints. Neutral: no country,
 * no from-address baked in.
 */
import { type Result, ok, err } from '@vello/core';
import {
  type IntegrationsApi,
  type EmailIntegrationView,
  type SetEmailIntegrationInput,
  type SendTestEmailResult,
  type SendTestOtpResult,
  type SetSmsIntegrationInput,
  type SetWhatsappIntegrationInput,
  type SmsHttpAuth,
  type SmsIntegrationView,
  type PushFcmSlotView,
  type PushIntegrationView,
  type SetPushFcmInput,
  type SetStorageIntegrationInput,
  type StorageIntegrationView,
  type StorageTestResult,
  type WhatsappIntegrationView,
  type WhatsappTemplateRef,
  type ApiError,
} from '@vello/api-client';

/** The email-integration form as the operator types it. `apiKey` is write-only (blank keeps the stored key). */
export interface EmailIntegrationForm {
  readonly fromEmail: string;
  readonly fromName: string;
  readonly replyTo: string;
  readonly enabled: boolean;
  /** A new API key to seal; blank keeps the currently stored key. Never populated from the server. */
  readonly apiKey: string;
}

export type EmailFormField = 'fromEmail' | 'replyTo';
export interface EmailFormIssue {
  readonly field: EmailFormField;
  readonly code: 'INVALID_EMAIL';
}

export function emptyEmailForm(): EmailIntegrationForm {
  return { fromEmail: '', fromName: '', replyTo: '', enabled: false, apiKey: '' };
}

/** Seed the editable form from the server status. The key is never returned, so `apiKey` stays blank. */
export function emailFormFromView(view: EmailIntegrationView): EmailIntegrationForm {
  return {
    fromEmail: view.config.fromEmail ?? '',
    fromName: view.config.fromName ?? '',
    replyTo: view.config.replyTo ?? '',
    enabled: view.config.enabled === true,
    apiKey: '',
  };
}

// A permissive address shape (one @, a dotted domain, no whitespace) — deliverability is proven by the test send.
const EMAIL = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

/** Validate + normalize into the wire input, or return every field issue at once (pure; tested in Node). */
export function normalizeEmailForm(form: EmailIntegrationForm): Result<SetEmailIntegrationInput, readonly EmailFormIssue[]> {
  const issues: EmailFormIssue[] = [];
  const fromEmail = form.fromEmail.trim();
  if (!EMAIL.test(fromEmail)) issues.push({ field: 'fromEmail', code: 'INVALID_EMAIL' });
  const replyTo = form.replyTo.trim();
  if (replyTo !== '' && !EMAIL.test(replyTo)) issues.push({ field: 'replyTo', code: 'INVALID_EMAIL' });
  if (issues.length > 0) return err(issues);
  const fromName = form.fromName.trim();
  const apiKey = form.apiKey.trim();
  return ok({
    fromEmail,
    ...(fromName !== '' ? { fromName } : {}),
    ...(replyTo !== '' ? { replyTo } : {}),
    enabled: form.enabled,
    ...(apiKey !== '' ? { apiKey } : {}),
  });
}

// ---- runtime seams (thin wrappers over the real operator integration endpoints) ------------------

interface IntegrationsRuntime {
  readonly integrations: IntegrationsApi;
}

/** Read the masked email-integration status + config. */
export function getEmailIntegration(runtime: IntegrationsRuntime): Promise<Result<EmailIntegrationView, ApiError>> {
  return runtime.integrations.getEmailConfig();
}

/** Set the email config and (optionally) rotate the key. */
export function setEmailIntegration(runtime: IntegrationsRuntime, input: SetEmailIntegrationInput): Promise<Result<EmailIntegrationView, ApiError>> {
  return runtime.integrations.setEmailConfig(input);
}

/** Send a test email (to `to`, or the operator's own email when omitted). */
export function sendTestEmail(runtime: IntegrationsRuntime, to?: string): Promise<Result<SendTestEmailResult, ApiError>> {
  return runtime.integrations.sendTestEmail(to !== undefined && to.trim() !== '' ? { to: to.trim() } : {});
}

// ---- OTP delivery slots (D320): WhatsApp Authentication + the SMS fallback gateway --------------------------

const LOCALE_TAG = /^[A-Za-z]{2,3}(?:[-_][A-Za-z0-9]{2,8})*$/;
const CURRENCY = /^[A-Z]{3}$/;
const E164 = /^\+[1-9]\d{6,14}$/;

const parseJsonObject = (text: string): Record<string, unknown> | undefined => {
  try {
    const v: unknown = JSON.parse(text);
    return typeof v === 'object' && v !== null && !Array.isArray(v) ? (v as Record<string, unknown>) : undefined;
  } catch {
    return undefined;
  }
};

/** Parse a `{ minor, currency }` pair from the form: both blank ⇒ no cost; else an integer + ISO 4217. */
function normalizeCost(minor: string, currency: string): Result<{ approxCostMinor?: number; approxCostCurrency?: string }, 'INVALID_COST'> {
  const m = minor.trim();
  const c = currency.trim().toUpperCase();
  if (m === '' && c === '') return ok({});
  const n = Number(m);
  if (!Number.isInteger(n) || n < 0 || !CURRENCY.test(c)) return err('INVALID_COST');
  return ok({ approxCostMinor: n, approxCostCurrency: c });
}

/** The WhatsApp slot form. `accessToken` is write-only (blank keeps the stored token). Templates are edited as JSON. */
export interface WhatsappIntegrationForm {
  readonly phoneNumberId: string;
  readonly businessAccountId: string;
  readonly apiVersion: string;
  /** `{ "pt": { "name": "vello_authentication_pt", "language": "pt_PT" }, … }` */
  readonly templatesJson: string;
  readonly defaultLocale: string;
  readonly approxCostMinor: string;
  readonly approxCostCurrency: string;
  readonly enabled: boolean;
  readonly accessToken: string;
}

export type WhatsappFormField = 'phoneNumberId' | 'businessAccountId' | 'apiVersion' | 'templatesJson' | 'defaultLocale' | 'approxCost';
export interface WhatsappFormIssue {
  readonly field: WhatsappFormField;
  readonly code: 'REQUIRED' | 'INVALID_VALUE' | 'INVALID_JSON' | 'INVALID_TEMPLATES' | 'INVALID_DEFAULT_LOCALE' | 'INVALID_COST';
}

export function emptyWhatsappForm(): WhatsappIntegrationForm {
  return { phoneNumberId: '', businessAccountId: '', apiVersion: 'v21.0', templatesJson: '', defaultLocale: '', approxCostMinor: '', approxCostCurrency: '', enabled: false, accessToken: '' };
}

export function whatsappFormFromView(view: WhatsappIntegrationView): WhatsappIntegrationForm {
  const c = view.config;
  return {
    phoneNumberId: c.phoneNumberId ?? '',
    businessAccountId: c.businessAccountId ?? '',
    apiVersion: c.apiVersion ?? 'v21.0',
    templatesJson: c.templates !== undefined ? JSON.stringify(c.templates, null, 2) : '',
    defaultLocale: c.defaultLocale ?? '',
    approxCostMinor: c.approxCostMinor !== undefined ? String(c.approxCostMinor) : '',
    approxCostCurrency: c.approxCostCurrency ?? '',
    enabled: c.enabled === true,
    accessToken: '',
  };
}

function parseTemplates(text: string): Record<string, WhatsappTemplateRef> | undefined {
  const o = parseJsonObject(text);
  if (o === undefined) return undefined;
  const out: Record<string, WhatsappTemplateRef> = {};
  for (const [locale, raw] of Object.entries(o)) {
    if (!LOCALE_TAG.test(locale) || typeof raw !== 'object' || raw === null) return undefined;
    const t = raw as Record<string, unknown>;
    if (typeof t.name !== 'string' || t.name.trim() === '' || typeof t.language !== 'string' || t.language.trim() === '') return undefined;
    out[locale] = { name: t.name.trim(), language: t.language.trim() };
  }
  return out;
}

/** Validate + normalize the WhatsApp form into the wire input, or return every field issue at once. */
export function normalizeWhatsappForm(form: WhatsappIntegrationForm): Result<SetWhatsappIntegrationInput, readonly WhatsappFormIssue[]> {
  const issues: WhatsappFormIssue[] = [];
  const phoneNumberId = form.phoneNumberId.trim();
  if (phoneNumberId === '') issues.push({ field: 'phoneNumberId', code: 'REQUIRED' });
  else if (!/^\d{5,32}$/.test(phoneNumberId)) issues.push({ field: 'phoneNumberId', code: 'INVALID_VALUE' });
  const businessAccountId = form.businessAccountId.trim();
  if (businessAccountId !== '' && !/^\d{5,32}$/.test(businessAccountId)) issues.push({ field: 'businessAccountId', code: 'INVALID_VALUE' });
  const apiVersion = form.apiVersion.trim() === '' ? 'v21.0' : form.apiVersion.trim();
  if (!/^v\d{1,3}\.\d{1,3}$/.test(apiVersion)) issues.push({ field: 'apiVersion', code: 'INVALID_VALUE' });
  let templates: Record<string, WhatsappTemplateRef> | undefined;
  if (form.templatesJson.trim() === '') issues.push({ field: 'templatesJson', code: 'REQUIRED' });
  else if (parseJsonObject(form.templatesJson) === undefined) issues.push({ field: 'templatesJson', code: 'INVALID_JSON' });
  else {
    templates = parseTemplates(form.templatesJson);
    if (templates === undefined || Object.keys(templates).length === 0) issues.push({ field: 'templatesJson', code: 'INVALID_TEMPLATES' });
  }
  const defaultLocale = form.defaultLocale.trim();
  if (defaultLocale === '') issues.push({ field: 'defaultLocale', code: 'REQUIRED' });
  else if (templates !== undefined && templates[defaultLocale] === undefined) issues.push({ field: 'defaultLocale', code: 'INVALID_DEFAULT_LOCALE' });
  const cost = normalizeCost(form.approxCostMinor, form.approxCostCurrency);
  if (!cost.ok) issues.push({ field: 'approxCost', code: 'INVALID_COST' });
  if (issues.length > 0 || templates === undefined || !cost.ok) return err(issues);
  const accessToken = form.accessToken.trim();
  return ok({
    phoneNumberId,
    ...(businessAccountId !== '' ? { businessAccountId } : {}),
    apiVersion,
    templates,
    defaultLocale,
    ...cost.value,
    enabled: form.enabled,
    ...(accessToken !== '' ? { accessToken } : {}),
  });
}

export type SmsAuthType = SmsHttpAuth['type'];

/** The SMS gateway form. `apiKey` is write-only. Body, headers and messages are edited as JSON. */
export interface SmsIntegrationForm {
  readonly endpoint: string;
  readonly method: 'POST' | 'PUT';
  readonly encoding: 'json' | 'form';
  readonly authType: SmsAuthType;
  /** The basic username / header name / query name / body field, per `authType`. */
  readonly authName: string;
  readonly authPrefix: string;
  readonly headersJson: string;
  /** `{ "to": "{to}", "text": "{text}" }` */
  readonly bodyJson: string;
  readonly from: string;
  /** `{ "pt": "Seu código Vello: {code}", … }` */
  readonly messagesJson: string;
  readonly defaultLocale: string;
  readonly messageIdPath: string;
  readonly successPath: string;
  /** Comma-separated accepted values at `successPath`. */
  readonly successValues: string;
  readonly errorCodePath: string;
  readonly approxCostMinor: string;
  readonly approxCostCurrency: string;
  readonly enabled: boolean;
  readonly apiKey: string;
}

export type SmsFormField = 'endpoint' | 'auth' | 'headersJson' | 'bodyJson' | 'messagesJson' | 'defaultLocale' | 'messageIdPath' | 'successCheck' | 'errorCodePath' | 'approxCost';
export interface SmsFormIssue {
  readonly field: SmsFormField;
  readonly code: 'REQUIRED' | 'INVALID_VALUE' | 'INVALID_JSON' | 'MISSING_PLACEHOLDER' | 'INVALID_DEFAULT_LOCALE' | 'INVALID_COST';
}

export function emptySmsForm(): SmsIntegrationForm {
  return { endpoint: '', method: 'POST', encoding: 'json', authType: 'bearer', authName: '', authPrefix: '', headersJson: '', bodyJson: '', from: '', messagesJson: '', defaultLocale: '', messageIdPath: '', successPath: '', successValues: '', errorCodePath: '', approxCostMinor: '', approxCostCurrency: '', enabled: false, apiKey: '' };
}

export function smsFormFromView(view: SmsIntegrationView): SmsIntegrationForm {
  const c = view.config;
  const auth = c.auth;
  const authName = auth === undefined ? '' : auth.type === 'basic' ? auth.username : auth.type === 'header' || auth.type === 'query' ? auth.name : auth.type === 'body' ? auth.field : '';
  return {
    endpoint: c.endpoint ?? '',
    method: c.method ?? 'POST',
    encoding: c.encoding ?? 'json',
    authType: auth?.type ?? 'bearer',
    authName,
    authPrefix: auth !== undefined && auth.type === 'header' ? (auth.prefix ?? '') : '',
    headersJson: c.headers !== undefined && Object.keys(c.headers).length > 0 ? JSON.stringify(c.headers, null, 2) : '',
    bodyJson: c.body !== undefined ? JSON.stringify(c.body, null, 2) : '',
    from: c.from ?? '',
    messagesJson: c.messages !== undefined ? JSON.stringify(c.messages, null, 2) : '',
    defaultLocale: c.defaultLocale ?? '',
    messageIdPath: c.messageIdPath ?? '',
    successPath: c.successPath ?? '',
    successValues: c.successValues?.join(', ') ?? '',
    errorCodePath: c.errorCodePath ?? '',
    approxCostMinor: c.approxCostMinor !== undefined ? String(c.approxCostMinor) : '',
    approxCostCurrency: c.approxCostCurrency ?? '',
    enabled: c.enabled === true,
    apiKey: '',
  };
}

function stringMap(text: string, keyPattern?: RegExp): Record<string, string> | undefined {
  const o = parseJsonObject(text);
  if (o === undefined) return undefined;
  const out: Record<string, string> = {};
  for (const [k, v] of Object.entries(o)) {
    if (typeof v !== 'string' || (keyPattern !== undefined && !keyPattern.test(k))) return undefined;
    out[k] = v;
  }
  return out;
}

/** Validate + normalize the SMS form into the wire input, or return every field issue at once. */
export function normalizeSmsForm(form: SmsIntegrationForm): Result<SetSmsIntegrationInput, readonly SmsFormIssue[]> {
  const issues: SmsFormIssue[] = [];
  const endpoint = form.endpoint.trim();
  if (endpoint === '') issues.push({ field: 'endpoint', code: 'REQUIRED' });
  else if (!/^https:\/\/\S+$/i.test(endpoint)) issues.push({ field: 'endpoint', code: 'INVALID_VALUE' });
  const authName = form.authName.trim();
  let auth: SmsHttpAuth | undefined;
  switch (form.authType) {
    case 'bearer':
      auth = { type: 'bearer' };
      break;
    case 'basic':
      auth = authName !== '' ? { type: 'basic', username: authName } : undefined;
      break;
    case 'header':
      auth = authName !== '' ? { type: 'header', name: authName, ...(form.authPrefix !== '' ? { prefix: form.authPrefix } : {}) } : undefined;
      break;
    case 'query':
      auth = authName !== '' ? { type: 'query', name: authName } : undefined;
      break;
    case 'body':
      auth = authName !== '' ? { type: 'body', field: authName } : undefined;
      break;
  }
  if (auth === undefined) issues.push({ field: 'auth', code: 'REQUIRED' });
  const headers = form.headersJson.trim() === '' ? undefined : stringMap(form.headersJson);
  if (form.headersJson.trim() !== '' && headers === undefined) issues.push({ field: 'headersJson', code: 'INVALID_JSON' });
  let body: Record<string, string> | undefined;
  if (form.bodyJson.trim() === '') issues.push({ field: 'bodyJson', code: 'REQUIRED' });
  else {
    body = stringMap(form.bodyJson);
    if (body === undefined) issues.push({ field: 'bodyJson', code: 'INVALID_JSON' });
    else {
      const values = Object.values(body);
      if (!values.some((v) => v.includes('{text}')) || !values.some((v) => v.includes('{to}') || v.includes('{toDigits}'))) issues.push({ field: 'bodyJson', code: 'MISSING_PLACEHOLDER' });
    }
  }
  let messages: Record<string, string> | undefined;
  if (form.messagesJson.trim() === '') issues.push({ field: 'messagesJson', code: 'REQUIRED' });
  else {
    messages = stringMap(form.messagesJson, LOCALE_TAG);
    if (messages === undefined || Object.keys(messages).length === 0) issues.push({ field: 'messagesJson', code: 'INVALID_JSON' });
    else if (!Object.values(messages).every((m) => m.includes('{code}'))) issues.push({ field: 'messagesJson', code: 'MISSING_PLACEHOLDER' });
  }
  const defaultLocale = form.defaultLocale.trim();
  if (defaultLocale === '') issues.push({ field: 'defaultLocale', code: 'REQUIRED' });
  else if (messages !== undefined && messages[defaultLocale] === undefined) issues.push({ field: 'defaultLocale', code: 'INVALID_DEFAULT_LOCALE' });
  const messageIdPath = form.messageIdPath.trim();
  if (messageIdPath !== '' && !/^[\w.-]{1,128}$/.test(messageIdPath)) issues.push({ field: 'messageIdPath', code: 'INVALID_VALUE' });
  const successPath = form.successPath.trim();
  const successValues = form.successValues.split(',').map((v) => v.trim()).filter((v) => v !== '');
  if (successPath !== '' && !/^[\w.-]{1,128}$/.test(successPath)) issues.push({ field: 'successCheck', code: 'INVALID_VALUE' });
  else if ((successPath === '') !== (successValues.length === 0) || successValues.length > 16 || successValues.some((v) => v.length > 64)) issues.push({ field: 'successCheck', code: 'INVALID_VALUE' });
  const errorCodePath = form.errorCodePath.trim();
  if (errorCodePath !== '' && !/^[\w.-]{1,128}$/.test(errorCodePath)) issues.push({ field: 'errorCodePath', code: 'INVALID_VALUE' });
  const cost = normalizeCost(form.approxCostMinor, form.approxCostCurrency);
  if (!cost.ok) issues.push({ field: 'approxCost', code: 'INVALID_COST' });
  if (issues.length > 0 || auth === undefined || body === undefined || messages === undefined || !cost.ok) return err(issues);
  const from = form.from.trim();
  const apiKey = form.apiKey.trim();
  return ok({
    endpoint,
    method: form.method,
    encoding: form.encoding,
    auth,
    ...(headers !== undefined ? { headers } : {}),
    body,
    ...(from !== '' ? { from } : {}),
    messages,
    defaultLocale,
    ...(messageIdPath !== '' ? { messageIdPath } : {}),
    ...(successPath !== '' ? { successPath, successValues } : {}),
    ...(errorCodePath !== '' ? { errorCodePath } : {}),
    ...cost.value,
    enabled: form.enabled,
    ...(apiKey !== '' ? { apiKey } : {}),
  });
}

/** A test destination must be an E.164 phone the operator owns. */
export function isTestPhone(value: string): boolean {
  return E164.test(value.trim());
}

// ---- runtime seams --------------------------------------------------------------------------------

export function getWhatsappIntegration(runtime: IntegrationsRuntime): Promise<Result<WhatsappIntegrationView, ApiError>> {
  return runtime.integrations.getWhatsappConfig();
}
export function setWhatsappIntegration(runtime: IntegrationsRuntime, input: SetWhatsappIntegrationInput): Promise<Result<WhatsappIntegrationView, ApiError>> {
  return runtime.integrations.setWhatsappConfig(input);
}
export function sendTestWhatsapp(runtime: IntegrationsRuntime, to: string, locale?: string): Promise<Result<SendTestOtpResult, ApiError>> {
  return runtime.integrations.sendTestWhatsapp({ to: to.trim(), ...(locale !== undefined && locale !== '' ? { locale } : {}) });
}
export function getSmsIntegration(runtime: IntegrationsRuntime): Promise<Result<SmsIntegrationView, ApiError>> {
  return runtime.integrations.getSmsConfig();
}
export function setSmsIntegration(runtime: IntegrationsRuntime, input: SetSmsIntegrationInput): Promise<Result<SmsIntegrationView, ApiError>> {
  return runtime.integrations.setSmsConfig(input);
}
export function sendTestSms(runtime: IntegrationsRuntime, to: string, locale?: string): Promise<Result<SendTestOtpResult, ApiError>> {
  return runtime.integrations.sendTestSms({ to: to.trim(), ...(locale !== undefined && locale !== '' ? { locale } : {}) });
}

// ---- D727: Armazenamento - the object store's real bucket (Cloudflare R2) -------------------------------------------------

/** The storage form as the operator types it. The key pair is write-only: both blank keeps the stored pair; both filled rotates it. */
export interface StorageIntegrationForm {
  readonly accountId: string;
  readonly bucket: string;
  readonly enabled: boolean;
  readonly accessKeyId: string;
  readonly secretAccessKey: string;
}

export type StorageFormField = 'accountId' | 'bucket' | 'keyPair';
export interface StorageFormIssue {
  readonly field: StorageFormField;
  readonly code: 'INVALID_ACCOUNT_ID' | 'INVALID_BUCKET' | 'KEY_PAIR_INCOMPLETE' | 'KEY_PAIR_REQUIRED';
}

export function emptyStorageForm(): StorageIntegrationForm {
  return { accountId: '', bucket: '', enabled: false, accessKeyId: '', secretAccessKey: '' };
}

/** Seed the form from the server view: the key pair is never returned, so both key fields stay blank. */
export function storageFormFromView(view: StorageIntegrationView): StorageIntegrationForm {
  return { accountId: view.accountId ?? '', bucket: view.bucket ?? '', enabled: view.enabled, accessKeyId: '', secretAccessKey: '' };
}

// The same shapes the server checks (`@vello/object-storage` R2_ACCOUNT_ID / S3_BUCKET_NAME); the server stays the authority.
const R2_ACCOUNT = /^[a-f0-9]{32}$/;
const BUCKET = /^[a-z0-9][a-z0-9-]{1,61}[a-z0-9]$/;

/** Validate + normalize into the wire input, or every field issue at once. `configured`: a pair is already sealed. */
export function normalizeStorageForm(form: StorageIntegrationForm, configured: boolean): Result<SetStorageIntegrationInput, readonly StorageFormIssue[]> {
  const issues: StorageFormIssue[] = [];
  const accountId = form.accountId.trim().toLowerCase();
  if (!R2_ACCOUNT.test(accountId)) issues.push({ field: 'accountId', code: 'INVALID_ACCOUNT_ID' });
  const bucket = form.bucket.trim();
  if (!BUCKET.test(bucket)) issues.push({ field: 'bucket', code: 'INVALID_BUCKET' });
  const accessKeyId = form.accessKeyId.trim();
  const secretAccessKey = form.secretAccessKey.trim();
  if ((accessKeyId === '') !== (secretAccessKey === '')) issues.push({ field: 'keyPair', code: 'KEY_PAIR_INCOMPLETE' });
  else if (form.enabled && accessKeyId === '' && !configured) issues.push({ field: 'keyPair', code: 'KEY_PAIR_REQUIRED' });
  if (issues.length > 0) return err(issues);
  return ok({ accountId, bucket, enabled: form.enabled, ...(accessKeyId !== '' ? { accessKeyId, secretAccessKey } : {}) });
}

export function getStorageIntegration(runtime: IntegrationsRuntime): Promise<Result<StorageIntegrationView, ApiError>> {
  return runtime.integrations.getStorageConfig();
}
export function setStorageIntegration(runtime: IntegrationsRuntime, input: SetStorageIntegrationInput): Promise<Result<StorageIntegrationView, ApiError>> {
  return runtime.integrations.setStorageConfig(input);
}
export function testStorageIntegration(runtime: IntegrationsRuntime): Promise<Result<StorageTestResult, ApiError>> {
  return runtime.integrations.testStorage();
}

// ---- D854 (L7a-033): the push provider (Firebase Cloud Messaging) ---------------------------------------------------

/** The push form as the operator types it. The service-account key is write-only: blank keeps the stored key; pasted JSON rotates it. */
export interface PushIntegrationForm {
  readonly enabled: boolean;
  readonly serviceAccount: string;
}

export type PushFormField = 'serviceAccount';
export interface PushFormIssue {
  readonly field: PushFormField;
  readonly code: 'KEY_NOT_JSON' | 'KEY_NOT_SERVICE_ACCOUNT' | 'KEY_REQUIRED';
}

export function emptyPushForm(): PushIntegrationForm {
  return { enabled: false, serviceAccount: '' };
}

/** Seed the form from the server view: the key is never returned, so the key field stays blank. */
export function pushFormFromView(view: PushFcmSlotView): PushIntegrationForm {
  return { enabled: view.enabled, serviceAccount: '' };
}

/**
 * Validate + normalize into the wire input, or the issue. A Firebase service-account key is a JSON object with `project_id`, `client_email` and
 * `private_key`; the server re-checks it and is the authority. `configured`: a key is already sealed (a blank field then keeps it).
 */
export function normalizePushForm(form: PushIntegrationForm, configured: boolean): Result<SetPushFcmInput, readonly PushFormIssue[]> {
  const raw = form.serviceAccount.trim();
  if (raw === '') {
    return form.enabled && !configured ? err([{ field: 'serviceAccount', code: 'KEY_REQUIRED' }]) : ok({ enabled: form.enabled });
  }
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return err([{ field: 'serviceAccount', code: 'KEY_NOT_JSON' }]);
  }
  const o = typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed) ? (parsed as Record<string, unknown>) : undefined;
  const text = (k: string): boolean => typeof o?.[k] === 'string' && (o[k] as string).trim() !== '';
  if (o === undefined || !text('project_id') || !text('client_email') || !text('private_key')) return err([{ field: 'serviceAccount', code: 'KEY_NOT_SERVICE_ACCOUNT' }]);
  return ok({ enabled: form.enabled, serviceAccount: raw });
}

export function getPushIntegration(runtime: IntegrationsRuntime): Promise<Result<PushIntegrationView, ApiError>> {
  return runtime.integrations.getPushIntegration();
}
export function setPushIntegration(runtime: IntegrationsRuntime, input: SetPushFcmInput): Promise<Result<PushFcmSlotView, ApiError>> {
  return runtime.integrations.setPushFcm(input);
}
